import { useEffect } from 'react'

interface TourFinishAnimationProps {
  onComplete: () => void
  durationMs?: number
}

/**
 * TourFinishAnimation — full-screen finish moment after the user clicks
 * the last step's CTA. Mirrors the UpdateAvailableModal SlashIconBox
 * vocabulary (halo + sweep + spinning slash) so the brand language stays
 * consistent. Holds for `durationMs` (default 1500ms) then calls
 * `onComplete`, which the parent FirstLoginTour wires to a hard reload —
 * the dashboard then mounts fresh post-tour with no lingering state.
 */
export default function TourFinishAnimation({
  onComplete,
  durationMs = 1500,
}: TourFinishAnimationProps) {
  useEffect(() => {
    const id = window.setTimeout(onComplete, durationMs)
    return () => window.clearTimeout(id)
  }, [onComplete, durationMs])

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/95 backdrop-blur-[16px]"
      role="status"
      aria-live="polite"
    >
      {/* Slash icon box — same vocabulary as UpdateAvailableModal SlashIconBox */}
      <div className="relative mb-8 flex h-20 w-20 items-center justify-center overflow-hidden rounded-[22px] border border-[var(--bn-accent-border-bold)] bg-[var(--bn-accent-bg-strong)] shadow-[var(--bn-cta-shadow)]">
        {/* Halo violet derrière */}
        <span
          aria-hidden="true"
          className="absolute -inset-[14px] -z-10 rounded-[34px] bg-[var(--bn-accent-bg)] [animation:bc-icon-halo_3s_ease-in-out_infinite]"
        />
        {/* Sweep diagonal */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-[-100%] top-0 z-[1] h-full w-[60%] skew-x-[-20deg]"
          style={{
            background:
              'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)',
            animation: 'bc-icon-beam-sweep 4s ease-in-out infinite 2s',
          }}
        />
        {/* Slash blanc qui tourne */}
        <svg
          aria-hidden="true"
          viewBox="0 0 32 32"
          fill="none"
          className="relative z-[2] block h-[56px] w-[56px] text-white drop-shadow-[0_0_14px_rgba(255,255,255,0.5)] drop-shadow-[0_0_28px_rgba(255,255,255,0.25)] [animation:bc-slash-spin_800ms_cubic-bezier(0.4,0,0.2,1)_infinite]"
          style={{ transformOrigin: 'center' }}
        >
          <line
            x1="11"
            y1="26"
            x2="21"
            y2="6"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Texte premium */}
      <p className="text-[18px] font-light tracking-tight text-white text-center max-w-md px-6 sm:text-[20px]">
        Bienvenue chez better
        <span style={{ color: '#A78BFA', fontWeight: 500 }}>/</span>
        close.
      </p>
    </div>
  )
}
